import React, {useState, useEffect} from 'react';
import Avatar from 'react-avatar';
import moment from 'moment-timezone';
import {BoltIcon, CalendarIcon} from '@heroicons/react-v2/24/solid';
import _ from 'lodash';

import Tooltip from '../components/Tooltip';
import {InterviewTypeIcon} from './InterviewTypeIcon';
import Link from '../components/Link';
import Button, {ButtonTypes} from '../components/ButtonNew';
import {
  Table, THead, TBody, TD, TH,
} from '../components/Markdown';
import {DotIcon} from '../components/svg';
import {requestIntro} from '../api/api';
import {
  formatTime,
  formatRelativeSessionTime,
  getRelativeTimeForPreviousDuetSession,
  toDuetInterviewTypeName,
  createURLWithParams,
  cx,
} from '../util';
import {
  LEADING_JOIN_TIME,
  WINDOW_START_TIME,
  InterviewTypes,
  InterviewTypesWith1PAIQuestionsFromCMS,
  InterviewModes,
  PartnerIntroStatus,
} from '../../../lib/constants/duet';

const SESSIONS_PAGE_SIZE = 6;

export const UpcomingRequestControls = ({request, onClickJoin, onClickCancel}) => {
  const [countdown, setCountdown] = useState(Infinity);

  useEffect(() => {
    const iid = setInterval(() => {
      setCountdown(moment(request.sessionTime).diff(moment(), 'seconds'));
    }, 1000);

    return () => clearInterval(iid);
  }, []);

  return (
    <div className="flex justify-end pr-1">
      {countdown <= WINDOW_START_TIME && countdown > LEADING_JOIN_TIME && (
        <span
          className="items-center px-2.5 py-0.5 rounded-full font-bold bg-indigo-100 text-indigo self-center whitespace-nowrap"
          style={{width: '145px'}}
        >
          <DotIcon />
          {`Live in ${formatTime(countdown)}`}
        </span>
      )}
      {countdown > LEADING_JOIN_TIME && (
        <Button
          onClick={onClickCancel}
          buttonType={ButtonTypes.TERTIARY}
          className="-mr-3 whitespace-nowrap"
        >
          Cancel session
        </Button>
      )}
      {countdown <= LEADING_JOIN_TIME && (
        <Button
          onClick={onClickJoin}
          className="whitespace-nowrap"
        >
          Join session
        </Button>
      )}
    </div>
  );
};

const BrowseQuestionsLink = ({type}) => (
  <Tooltip
    content={`${toDuetInterviewTypeName(type)} interviews don't have pre-assigned questions. Prepare the question you'll ask ahead of time`}
    placement="top"
  >
    <Link href={createURLWithParams('/questions', {...InterviewTypes[type]?.tags || {}, src: 'practice'})}>
      <span className="text-indigo font-medium">Browse questions</span>
    </Link>
  </Tooltip>
);

const truncateQuestion = (title) => _.truncate(title, {length: 40, separator: ' '});

const pastSessionHasQuestion = (session) => (session.mode === InterviewModes.AI
  ? Boolean(session.question)
  : Boolean(session.partnerQuestion || session.question));

const PastQuestionLink = ({session}) => {
  if (session.mode === InterviewModes.AI) {
    if (typeof session.question === 'string') {
      return <span className="italic">{truncateQuestion(session.question)}</span>;
    }
    if (InterviewTypesWith1PAIQuestionsFromCMS[session.type]) {
      return (
        <Link
          href={`/practice/prepare/${session.question.slug}?ai=${session.room}`}
          className="text-indigo font-semibold italic"
        >
          {truncateQuestion(session.question.title)}
        </Link>
      );
    }
    return (
      <Link
        href={`/questions/${session.question.id}/${session.question.slug}?src=ai_session`}
        className="text-indigo font-semibold italic"
      >
        {truncateQuestion(session.question.data.title)}
      </Link>
    );
  }

  const question = session.partnerQuestion || session.question;
  if (typeof question === 'string') {
    return <span className="italic">{truncateQuestion(question)}</span>;
  }
  return (
    <Link
      href={`/practice/prepare/${question.slug}`}
      className="text-indigo font-semibold italic"
    >
      {truncateQuestion(question.title)}
    </Link>
  );
};

const AIPartnerCell = ({className}) => (
  <div className={cx('flex', className)}>
    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-0.5 text-sm font-medium text-indigo-700">
      <BoltIcon className="h-3.5 w-3.5" />
      AI
    </span>
  </div>
);

const FriendPartnerCell = ({session, className}) => {
  const {partner} = session;
  return (
    <div className={cx('flex items-center', className)}>
      {partner && (
        <Link href={`/users/${partner.uuid}`} className="mr-2.5">
          <Avatar
            name={partner.displayName}
            src={partner.profileImage}
            color="#f0f0f0"
            fgColor="#000000"
            size={32}
            round
          />
        </Link>
      )}
      <div>
        {partner && (
          <Link href={`/users/${partner.uuid}`} className="block text-gray-700 font-medium">
            {partner.displayName}
          </Link>
        )}
        <div className="text-gray-500">Practice with a friend</div>
      </div>
    </div>
  );
};

const PrampPartnerCell = ({session, className}) => session.partner && (
  <div className={cx('flex items-center', className)}>
    <Avatar
      name={session.partner.displayName}
      src={session.partner.profileImage}
      color="#f0f0f0"
      fgColor="#000000"
      size={32}
      round
      className="mr-2.5"
    />
    <div>
      <div className="text-gray-700 font-medium">
        {session.partner.displayName}
      </div>
      {session.introStatus === PartnerIntroStatus.ACCEPTED && (
      <div>{session.partner.email}</div>
      )}
    </div>
  </div>
);

const MockPartnerCell = ({session, className, showIntroActions = true}) => {
  const [status, setStatus] = useState(session.introStatus);
  const [partner, setPartner] = useState(session.partner);

  const handleRequestIntro = async () => {
    if (window.confirm('An introduction request will be emailed to your partner. If your partner accepts, we\'ll connect the two of you over email.')) {
      await requestIntro(session.room);
      setStatus(PartnerIntroStatus.REQUESTED);
    }
  };

  const handleAcceptIntro = async () => {
    if (window.confirm('When you accept this introduction, we\'ll send out an email to connect you directly.')) {
      const newPartner = await requestIntro(session.room);
      setStatus(PartnerIntroStatus.ACCEPTED);
      setPartner(newPartner);
    }
  };

  return (
    <div className={cx('flex items-center', className)}>
      <Link
        href={`/users/${partner.uuid}`}
        className="mr-2.5"
      >
        <Avatar
          name={partner.displayName}
          src={partner.profileImage}
          color="#f0f0f0"
          fgColor="#000000"
          size={32}
          round
        />
      </Link>
      <div>
        <Link
          href={`/users/${partner.uuid}`}
          className="block text-gray-700 font-medium"
        >
          {partner.displayName}
        </Link>
        {session.mode === InterviewModes.Scheduled && showIntroActions && (
          <div>
            {status === PartnerIntroStatus.NONE && (
              <Button
                onClick={handleRequestIntro}
                buttonType={ButtonTypes.TERTIARY}
                className="-mx-3 -my-2"
              >
                Request intro
              </Button>
            )}
            {status === PartnerIntroStatus.PENDING && (
              <Button
                onClick={handleAcceptIntro}
                buttonType={ButtonTypes.TERTIARY}
                className="-mx-3 -my-2"
              >
                Accept intro request
              </Button>
            )}
            {status === PartnerIntroStatus.REQUESTED && (
              <span>Request sent</span>
            )}
            {status === PartnerIntroStatus.ACCEPTED && partner && (
              <span>{partner.email}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const FeedbackCell = ({onViewFeedback}) => (
  <div className="flex justify-end">
    <Button
      onClick={onViewFeedback}
      buttonType={ButtonTypes.SECONDARY}
    >
      View feedback
    </Button>
  </div>
);

const SessionCell = ({row}) => {
  const {kind, data} = row;

  let secondLine = null;
  if (kind === 'upcoming') {
    if (data.question?.slug) {
      secondLine = (
        <span>
          <span className="text-gray-500">Asking </span>
          <Link
            href={`/practice/prepare/${data.question.slug}`}
            className="text-indigo font-semibold italic"
          >
            {truncateQuestion(data.question.title)}
          </Link>
        </span>
      );
    } else {
      secondLine = <BrowseQuestionsLink type={data.type} />;
    }
  } else if (pastSessionHasQuestion(data)) {
    secondLine = (
      <span>
        <span className="text-gray-500">Asked </span>
        <PastQuestionLink session={data} />
      </span>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <InterviewTypeIcon typeKey={data.type} size="sm" />
      <div className="min-w-0">
        <div className="text-gray-900 font-medium">{toDuetInterviewTypeName(data.type)}</div>
        {secondLine && <div>{secondLine}</div>}
      </div>
    </div>
  );
};

const PartnerCell = ({row, className, showIntroActions}) => {
  const {kind, data} = row;
  if (kind === 'upcoming') {
    return null;
  }
  const session = data;
  if (session.mode === InterviewModes.AI) return <AIPartnerCell className={className} />;
  if (session.mode === InterviewModes.OnDemand) {
    return <FriendPartnerCell session={session} className={className} />;
  }
  if (session.mode === InterviewModes.Pramp) {
    return <PrampPartnerCell session={session} className={className} />;
  }
  return (
    <MockPartnerCell session={session} className={className} showIntroActions={showIntroActions} />
  );
};

const WhenCell = ({row}) => {
  const {kind, data} = row;
  if (kind === 'upcoming') {
    return (
      <span className="flex items-center">
        {formatRelativeSessionTime(data.sessionTime)}
        <Tooltip content="Add to calendar" placement="top">
          <a title="Add to calendar" href={data.googleCalendarUrl} target="_blank" rel="noreferrer noopener">
            <span className="sr-only">Add to calendar</span>
            <CalendarIcon className="mb-1 ml-2 text-indigo-600 h-5 w-5" />
          </a>
        </Tooltip>
      </span>
    );
  }
  return <span>{getRelativeTimeForPreviousDuetSession(data)}</span>;
};

const SessionRow = ({row, showIntroActions, renderAction}) => (
  <tr>
    <TD><SessionCell row={row} /></TD>
    <TD><WhenCell row={row} /></TD>
    <TD><PartnerCell row={row} showIntroActions={showIntroActions} /></TD>
    <TD>{renderAction(row)}</TD>
  </tr>
);

const MobileSessionRow = ({row, showIntroActions, renderAction}) => (
  <Table className="border border-gray-100 mb-4">
    <THead>
      <tr>
        <TH colSpan="2"><WhenCell row={row} /></TH>
      </tr>
    </THead>
    <TBody>
      <tr>
        <TD colSpan="2"><SessionCell row={row} /></TD>
      </tr>
      {row.kind === 'past' && (
        <tr>
          <TD>Partner</TD>
          <TD className="text-gray-900"><PartnerCell row={row} className="justify-end" showIntroActions={showIntroActions} /></TD>
        </tr>
      )}
      <tr>
        <TD colSpan="2">{renderAction(row)}</TD>
      </tr>
    </TBody>
  </Table>
);

// Combined upcoming + past sessions, with upcoming first
const SessionsTable = ({
  upcomingRequests = [],
  prevSessions = [],
  showIntroActions = true,
  renderAction, // ({kind, data}) => node; the rightmost column of each row
}) => {
  const [page, setPage] = useState(0);

  // Key by position in the combined list to accommodate different data types,
  // ensure valid keys, and not break paging
  const rows = [
    ..._.sortBy(upcomingRequests, 'sessionTime').map((r) => ({kind: 'upcoming', data: r})),
    ...prevSessions.map((s) => ({kind: 'past', data: s})),
  ].map((row, index) => ({...row, key: `${row.kind}-${index}`}));

  if (rows.length === 0) return null;

  const pageCount = Math.ceil(rows.length / SESSIONS_PAGE_SIZE);
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * SESSIONS_PAGE_SIZE;
  const visibleRows = rows.slice(start, start + SESSIONS_PAGE_SIZE);

  return (
    <>
      <Table className="hidden md:table rounded-2xl">
        <THead>
          <tr>
            <TH className="border-b-0 text-xs font-semibold uppercase tracking-wider text-gray-400">Session</TH>
            <TH className="border-b-0 text-xs font-semibold uppercase tracking-wider text-gray-400">When</TH>
            <TH className="border-b-0 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {showIntroActions ? (
                <Tooltip
                  content="If both you and your peer ask to be introduced, we'll send out an email to connect you directly"
                  placement="top"
                >
                  <span>Partner</span>
                </Tooltip>
              ) : (
                <span>Partner</span>
              )}
            </TH>
            <TH className="border-b-0 text-xs font-semibold uppercase tracking-wider text-gray-400" />
          </tr>
        </THead>
        <TBody>
          {visibleRows.map((row) => (
            <SessionRow
              key={row.key}
              row={row}
              showIntroActions={showIntroActions}
              renderAction={renderAction}
            />
          ))}
        </TBody>
      </Table>
      <div className="block md:hidden">
        {visibleRows.map((row) => (
          <MobileSessionRow
            key={row.key}
            row={row}
            showIntroActions={showIntroActions}
            renderAction={renderAction}
          />
        ))}
      </div>
      {pageCount > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">
            {`Showing ${start + 1}–${Math.min(start + SESSIONS_PAGE_SIZE, rows.length)} of ${rows.length}`}
          </span>
          <div className="flex items-center gap-2">
            {safePage > 0 && (
              <Button
                buttonType={ButtonTypes.SECONDARY}
                onClick={() => setPage(safePage - 1)}
              >
                Previous
              </Button>
            )}
            {safePage < pageCount - 1 && (
              <Button
                buttonType={ButtonTypes.SECONDARY}
                onClick={() => setPage(safePage + 1)}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SessionsTable;
